import { useNavigate } from '@tanstack/react-router'
import { DetailCard } from '@trellis/roots/data'
import { Skeleton } from '@trellis/roots/skeleton'
import { Typography } from '@trellis/roots/typography'
import { ONBOARDING_CARD } from '@trellis/shared'
import { AlertCircle, ShieldCheck } from 'lucide-react'

import { useActionableNotificationsQuery } from '@/hooks/useNotifications'
import { useOnboardingStatusQuery } from '@/hooks/useOnboardingCards'
import { authClient } from '@/lib/auth-client'
import { cn } from '@/lib/utils'

import { getNavigationPath, getNotificationIcon } from './notification-utils'

export function NeedsAttentionCard() {
  const { data: result, isPending } = useActionableNotificationsQuery()
  const navigate = useNavigate()

  const { data: session } = authClient.useSession()
  const { data: onboardingResult } = useOnboardingStatusQuery()
  const dismissedCards = onboardingResult?.data?.dismissedCards

  const needsTwoFactor =
    !!dismissedCards &&
    !dismissedCards.includes(ONBOARDING_CARD.TWO_FACTOR_SECURITY) &&
    session?.user !== undefined &&
    !session.user.twoFactorEnabled

  const actionable = result?.data
  const items = actionable?.items ?? []
  const notificationCount = actionable?.count ?? 0
  const count = notificationCount + (needsTwoFactor ? 1 : 0)

  const description =
    count === 0
      ? 'Nothing needs your attention'
      : `${count} ${count === 1 ? 'item needs' : 'items need'} your attention`

  return (
    <DetailCard
      title="Needs attention"
      icon={AlertCircle}
      description={isPending ? undefined : description}
      headerAction={
        notificationCount > items.length && (
          <p className="text-muted-foreground text-xs">
            +{notificationCount - items.length} more
          </p>
        )
      }
      contentClassName="p-2"
    >
      {isPending ? (
        <div role="status" className="space-y-2 p-2">
          <span className="sr-only">Loading notifications</span>
          {Array.from({ length: 3 }, (_, index) => (
            <Skeleton key={index} className="h-10 w-full" aria-hidden="true" />
          ))}
        </div>
      ) : count === 0 ? (
        <Typography variant="body-sm" color="muted" className="p-2">
          You&apos;re all caught up.
        </Typography>
      ) : (
        <ul aria-label="Actionable notifications" className="space-y-1">
          {needsTwoFactor && (
            <li>
              <button
                type="button"
                onClick={() => void navigate({ to: '/2fa-setup' })}
                className="hover:bg-muted/50 flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2 py-2 text-left text-sm transition-colors"
              >
                <ShieldCheck
                  className="text-muted-foreground size-4 shrink-0"
                  aria-hidden="true"
                />
                <span className="min-w-0 flex-1 truncate">
                  Set up two-factor security
                </span>
                <span className="text-primary shrink-0 text-xs font-medium">Set up</span>
              </button>
            </li>
          )}
          {items.map((notification) => {
            const path = getNavigationPath(notification)
            return (
              <li key={notification.id}>
                <button
                  type="button"
                  onClick={() =>
                    path &&
                    /* getNavigationPath returns an untyped string, not the route-tree literal
                     * union `to` expects — `href` bypasses that check for a value already
                     * validated as a real path. */
                    navigate({ href: path })
                  }
                  className={cn(
                    'flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left text-sm transition-colors',
                    path && 'hover:bg-muted/50 cursor-pointer',
                    !path && 'cursor-default',
                  )}
                >
                  {getNotificationIcon(notification.type)}
                  <span className="min-w-0 flex-1 truncate">{notification.title}</span>
                  {notification.actionLabel && (
                    <span className="text-primary shrink-0 text-xs font-medium">
                      {notification.actionLabel}
                    </span>
                  )}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </DetailCard>
  )
}
